/**
 * Server-side chatbot response caching service using Supabase
 * Shared cache across ALL users to significantly reduce API costs
 */

import { supabase } from '../lib/supabase';

interface CachedResponse {
  id: string;
  question_normalized: string;
  question_original: string;
  response: string;
  hit_count: number;
  last_hit_at: string | null;
  created_at: string;
  expires_at: string;
}

interface CacheStats {
  total_entries: number;
  total_hits: number;
  avg_hits_per_entry: number;
  max_hits: number;
  active_entries: number;
  expired_entries: number;
  estimated_savings_gbp: number;
}

/**
 * Normalize question for cache key matching
 * Must match the normalization in backend
 */
const normalizeQuestion = (question: string): string => {
  return question
    .toLowerCase()
    .trim()
    .replace(/[?!.,;:]/g, '') // Remove punctuation
    .replace(/\s+/g, ' ') // Normalize whitespace
    .replace(/^(please|can you|could you|would you|can i|do you)\s+/i, '')
    .replace(/\s+(please|thanks|thank you)$/i, '');
};

/**
 * Get cached response from Supabase (shared across all users)
 * Returns null if not found or expired
 */
export const getCachedResponse = async (question: string): Promise<string | null> => {
  try {
    const normalized = normalizeQuestion(question);
    
    // Query Supabase for cached response - use maybeSingle() to avoid 406 error
    const { data, error } = await supabase
      .from('chatbot_cache')
      .select('id, response, hit_count, expires_at')
      .eq('question_normalized', normalized)
      .gt('expires_at', new Date().toISOString()) // Only non-expired
      .maybeSingle();
    
    if (error || !data) {
      return null;
    }
    
    // Update hit count asynchronously (don't wait)
    incrementHitCount(data.id, data.hit_count);
    
    console.log(`✅ Supabase cache hit for: "${question.substring(0, 50)}..." (saved £0.01)`);
    return data.response;
    
  } catch (error) {
    console.warn('Cache lookup error:', error);
    return null;
  }
};

/**
 * Increment hit count for analytics (fire and forget)
 */
const incrementHitCount = async (cacheId: string, currentCount: number): Promise<void> => {
  try {
    await supabase
      .from('chatbot_cache')
      .update({ 
        hit_count: currentCount + 1,
        last_hit_at: new Date().toISOString()
      })
      .eq('id', cacheId);
  } catch (error) {
    // Silent fail - analytics not critical
    console.warn('Failed to increment hit count:', error);
  }
};

/**
 * Store response in Supabase cache (shared across all users)
 */
export const cacheResponse = async (question: string, answer: string): Promise<void> => {
  try {
    const normalized = normalizeQuestion(question);
    
    // Get current session to track who created (optional)
    const { data: { session } } = await supabase.auth.getSession();
    
    const { error } = await supabase
      .from('chatbot_cache')
      .insert({
        question_normalized: normalized,
        question_original: question.trim(),
        response: answer.trim(),
        hit_count: 0,
        created_by: session?.user?.id || null,
        expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() // 24 hours
      });
    
    if (error) {
      // Conflict means it already exists (race condition) - that's fine
      if (error.code !== '23505') { // 23505 = unique violation
        console.warn('Failed to cache response:', error);
      }
    } else {
      console.log(`✅ Cached new response: "${question.substring(0, 50)}..."`);
    }
    
  } catch (error) {
    console.warn('Cache storage error:', error);
  }
};

/**
 * Get cache statistics for analytics
 */
export const getCacheStats = async (): Promise<CacheStats | null> => {
  try {
    const { data, error } = await supabase
      .from('chatbot_cache_stats')
      .select('*')
      .maybeSingle();
    
    if (error || !data) {
      return null;
    }
    
    return {
      total_entries: data.total_entries || 0,
      total_hits: data.total_hits || 0,
      avg_hits_per_entry: data.avg_hits_per_entry || 0,
      max_hits: data.max_hits || 0,
      active_entries: data.active_entries || 0,
      expired_entries: data.expired_entries || 0,
      estimated_savings_gbp: data.estimated_savings_gbp || 0
    };
    
  } catch (error) {
    console.warn('Failed to get cache stats:', error);
    return null;
  }
};

/**
 * Get cache hit rate percentage
 */
export const getCacheHitRate = async (): Promise<number> => {
  try {
    const stats = await getCacheStats();
    if (!stats || stats.total_hits === 0) return 0;
    
    // Estimate total requests (hits + approximate misses based on entries)
    const estimatedTotalRequests = stats.total_hits + stats.total_entries;
    return Math.round((stats.total_hits / estimatedTotalRequests) * 100);
    
  } catch {
    return 0;
  }
};

/**
 * Get most popular cached questions (admin feature)
 */
export const getTopCachedQuestions = async (limit: number = 10): Promise<CachedResponse[]> => {
  try {
    const { data, error } = await supabase
      .from('chatbot_cache')
      .select('*')
      .order('hit_count', { ascending: false })
      .limit(limit);
    
    if (error || !data) {
      return [];
    }
    
    return data;
    
  } catch (error) {
    console.warn('Failed to get top questions:', error);
    return [];
  }
};

/**
 * Clear expired cache entries (admin function)
 * Note: This is also done automatically by Supabase function
 */
export const clearExpiredCache = async (): Promise<number> => {
  try {
    const { data, error } = await supabase
      .from('chatbot_cache')
      .delete()
      .lt('expires_at', new Date().toISOString())
      .select();
    
    if (error) {
      console.warn('Failed to clear expired cache:', error);
      return 0;
    }
    
    return data?.length || 0;
    
  } catch (error) {
    console.warn('Failed to clear expired cache:', error);
    return 0;
  }
};

/**
 * Pre-warm check - verify common questions are cached
 * Note: Questions are pre-populated in Supabase migration
 */
export const verifyCachePrewarmed = async (): Promise<boolean> => {
  try {
    const commonQuestions = [
      'what modules do you have',
      'how much does it cost',
      'do you offer a free trial'
    ];
    
    for (const q of commonQuestions) {
      const { data } = await supabase
        .from('chatbot_cache')
        .select('id')
        .eq('question_normalized', q)
        .maybeSingle();
      
      if (!data) {
        console.warn(`Cache missing pre-warmed question: "${q}"`);
        return false;
      }
    }
    
    console.log('✅ Cache pre-warmed and verified');
    return true;
    
  } catch (error) {
    console.warn('Failed to verify cache pre-warm:', error);
    return false;
  }
};

/**
 * Export cache for analysis (admin feature)
 */
export const exportCache = async (): Promise<CachedResponse[]> => {
  try {
    const { data, error } = await supabase
      .from('chatbot_cache')
      .select('*')
      .order('hit_count', { ascending: false });
    
    if (error || !data) {
      return [];
    }
    
    return data;
    
  } catch (error) {
    console.warn('Failed to export cache:', error);
    return [];
  }
};
